import { client, gql } from 'data-provider';
import { EReturnStatus } from 'enums';
import { IReturn, ISale } from 'interfaces';

export interface IAllReturnQuery {
  startDate?: string;
  endDate?: string;
  searchString?: string;
  start?: number;
  limit?: number;
}

interface CreateReturnData {
  note?: string;
  client: string;
  salesCopy: string;
  returnDate: Date;
  warehouse: string;
  returnStatus: EReturnStatus;
  returnProducts: {
    productVariant: string;
    salesProductCopy: string;
    itemPrice: number;
    returnQuantity: number;
    lossAmount: number;
  }[];
  returnExpense: { id: string }[];
}

export const getReturns = async (returnQuery?: IAllReturnQuery) => {
  const where = {
    query: returnQuery && returnQuery.searchString ? returnQuery.searchString : '',
    startDate: returnQuery && returnQuery.startDate ? returnQuery.startDate : undefined,
    endDate: returnQuery && returnQuery.endDate ? returnQuery.endDate : undefined,
  };

  const query = gql`
    query ($sort: SortInput, $where: JSON, $start: Int, $limit: Int) {
      returnsCopy(sort: $sort, where: $where, start: $start, limit: $limit) {
        data {
          id
          returnDate
          returnStatus
          totalAmount
          client {
            name
          }
          warehouse {
            name
          }
          user {
            name
          }
        }
        total
        size
        page
      }
    }
  `;

  const variables = {
    where,
    start: returnQuery && returnQuery.start ? returnQuery.start : 1,
    limit: returnQuery && returnQuery.limit ? returnQuery.limit : 10,
  };
  const data = await client.request(query, variables);
  return data.returnsCopy as {
    data: IReturn[];
    page: number;
    size: number;
    total: number;
  };
};

export const getReturn = async (id: string) => {
  const query = gql`
    query ($id: ID!) {
      return(id: $id) {
        id
        returnDate
        returnStatus
        totalAmount
        user {
          id
          name
        }
        client {
          id
          name
        }
        warehouse {
          id
          name
        }
        returnProducts {
          id
          returnQuantity
          lossAmount
          subTotal
          salesProduct {
            unitPrice
            product {
              sku
              name
            }
          }
        }
      }
    }
  `;

  const variables = { id };
  const data = await client.request(query, variables);
  return data.return as IReturn;
};

export const createReturn = async (data: CreateReturnData) => {
  const mutation = gql`
    mutation ($input: createReturnCopyInput!) {
      createReturnCopy(input: $input) {
        id
      }
    }
  `;

  const variables = {
    input: { data },
  };

  const createdData = await client.request(mutation, variables);
  return createdData;
};

export const updateReturnStatus = async (id: string, returnStatus: EReturnStatus) => {
  const mutation = gql`
    mutation ($input: updateReturnStatusCopyInput!) {
      updateReturnCopyStatus(input: $input) {
        returnCopy {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
      data: { returnStatus },
    },
  };
  const updatedData = await client.request(mutation, variables);
  return updatedData;
};

export const deleteReturn = async (id: string) => {
  const mutation = gql`
    mutation ($input: deleteReturnCopyInput!) {
      deleteReturnCopy(input: $input) {
        returnCopy {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
    },
  };

  await client.request(mutation, variables);
};

export const getInvoicesWithProductsAndClient = async () => {
  const query = gql`
    query {
      salesDropdown {
        id
        client {
          id
          name
        }
        warehouse {
          name
          id
        }
        salesProducts {
          id
          quantity
          unitPrice
          subTotal
          product {
            id
            name
            sku
          }
        }
        salesDate
      }
    }
  `;

  const data = await client.request(query);
  return data.salesDropdown as ISale[];
};

export const getReturnsDropdown = async (startDate?: string, endDate?: string) => {
  const where = { startDate, endDate };
  const query = gql`
    query ($where: JSON) {
      returnDropdown(where: $where) {
        id
        returnDate
        returnStatus
        totalAmount
        returnProducts {
          id
          lossAmount
          returnQuantity
        }
        user {
          id
          name
        }
        client {
          id
          name
          email
        }
        sales {
          id
          salesDate
        }
        warehouse {
          id
          name
        }
      }
    }
  `;

  const variables = { where };

  const data = await client.request(query, variables);
  return data.returnDropdown as IReturn[];
};

export const getSalesFromWarehouse = async (warehouseId: string) => {
  const query = gql`
    query ($warehouse: String!) {
      salesListFromWarehouse(warehouse: $warehouse) {
        id
      }
    }
  `;

  const variables = { warehouse: warehouseId };
  const data = await client.request(query, variables);
  return data.salesListFromWarehouse as { id: string }[];
};
